import { 
  Crown, Award, Medal, Sparkles, BadgeCheck, Zap, 
  Network, Map, MapPinned, HeartHandshake, UserSquare2
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Badge category metadata - single source of truth aligned with seed data
export const BADGE_CATEGORY_METADATA = {
  loyalty: {
    name: 'Restaurant Loyalist',
    description: 'Loyalty to a specific restaurant based on orders + frequency + recency',
    icon: Crown,
    accentColor: '#C63D3D',
    tiers: [
      { name: 'Visitor', requirement: '1-2 orders' },
      { name: 'Regular', requirement: '3-6 orders' },
      { name: 'Insider', requirement: '7-12 orders + recent activity' },
      { name: 'Ambassador', requirement: '13+ orders + consistent recency' },
    ],
  },
  kudoz: {
    name: 'Kudoz',
    description: 'Lifetime Kudoz earned through reviews, engagement, and challenges',
    icon: Award,
    accentColor: '#2F64D0',
    tiers: [
      { name: 'Bronze', requirement: '1-20,000 Kudoz' },
      { name: 'Silver', requirement: '20,000-40,000 Kudoz' },
      { name: 'Gold', requirement: '40,000-80,000 Kudoz' },
      { name: 'Platinum', requirement: '80,000+ Kudoz' },
    ],
  },
  review: {
    name: 'Review Hero',
    description: 'Number of reviews written',
    icon: Medal,
    accentColor: '#FF8743',
    tiers: [
      { name: 'Reviewer', requirement: '1-4 reviews' },
      { name: 'Taster', requirement: '5-14 reviews' },
      { name: 'Food Critic', requirement: '15-39 reviews' },
      { name: 'Master Reviewer', requirement: '40+ reviews' },
    ],
  },
  trust: {
    name: 'Trusted Critic',
    description: 'Composite trust score based on trustworthy rating, review helpfulness, review activity, and social influence',
    icon: BadgeCheck,
    accentColor: '#2AB7A9',
    tiers: [
      { name: 'New Voice', requirement: '0-74 (building reputation)' },
      { name: 'Reliable Reviewer', requirement: '75-84 (trusted member)' },
      { name: 'Trusted Foodie', requirement: '85-94 (highly credible)' },
      { name: 'Elite Taster', requirement: '95-100 (community leader)' },
    ],
  },
  engagement: {
    name: 'Eatzy Explorer',
    description: 'Frequency of app usage: opens, sessions, activity streaks',
    icon: Zap,
    accentColor: '#7BD342',
    tiers: [
      { name: 'Casual Explorer', requirement: 'Occasional use' },
      { name: 'Frequent Diner', requirement: 'Weekly engagement' },
      { name: 'Daily Discoverer', requirement: '5+ days active per week' },
      { name: 'Power User', requirement: '20+ sessions/month for 3 months' },
    ],
  },
  social: {
    name: 'Foodie Socialite',
    description: 'Social connections and referrals',
    icon: Network,
    accentColor: '#FF4FA3',
    tiers: [
      { name: 'Friendly Diner', requirement: '1-2 friends' },
      { name: 'Foodie Connector', requirement: '3-6 friends' },
      { name: 'Community Builder', requirement: '7-14 friends' },
      { name: 'Tastemaker', requirement: '15+ friends & positive referral activity' },
    ],
  },
  order_diversity: {
    name: 'Restaurant Adventurer',
    description: 'Number of different restaurants ordered from',
    icon: Map,
    accentColor: '#FFD451',
    tiers: [
      { name: 'Sampler', requirement: '1-4 restaurants' },
      { name: 'Explorer', requirement: '5-9 restaurants' },
      { name: 'Trailblazer', requirement: '10-19 restaurants' },
      { name: 'Local Legend', requirement: '20+ restaurants' },
    ],
  },
  review_diversity: {
    name: 'Culinary Explorer',
    description: 'Number of different restaurants reviewed',
    icon: MapPinned,
    accentColor: '#2ED1E1',
    tiers: [
      { name: 'First Bite', requirement: '1-3 restaurants' },
      { name: 'Food Adventurer', requirement: '4-9 restaurants' },
      { name: 'City Explorer', requirement: '10-19 restaurants' },
      { name: 'Urban Food Guide', requirement: '20+ restaurants' },
    ],
  },
  tip: {
    name: 'Generous Tipper',
    description: 'Average driver tip percentage',
    icon: HeartHandshake,
    accentColor: '#8BC34A',
    tiers: [
      { name: 'Considerate', requirement: 'Avg tip 10-15%' },
      { name: 'Supporter', requirement: 'Avg tip 15-20%' },
      { name: 'Big-Hearted', requirement: 'Avg tip 20-24%' },
      { name: 'Top Patron', requirement: 'Avg tip 24%+' },
    ],
  },
  profile: {
    name: 'Profile Pro',
    description: 'Account data completion and verification steps',
    icon: UserSquare2,
    accentColor: '#2B3A67',
    tiers: [
      { name: 'Starter', requirement: '30% complete' },
      { name: 'Builder', requirement: '60% complete' },
      { name: 'Verified', requirement: '90% complete' },
      { name: 'Fully Dialed', requirement: '100% complete with optional extras' },
    ],
  },
} as const;

export type BadgeCategory = keyof typeof BADGE_CATEGORY_METADATA;

// Helper to get icon component for badge category
export function getBadgeCategoryIcon(category: string): LucideIcon {
  const metadata = BADGE_CATEGORY_METADATA[category as BadgeCategory];
  return metadata?.icon || Award;
}

// Helper to get category accent color
export function getCategoryAccentColor(category: string): string {
  const metadata = BADGE_CATEGORY_METADATA[category as BadgeCategory];
  return metadata?.accentColor || '#6b7280';
}

// Helper to get category display name
export function getCategoryName(category: string): string {
  const metadata = BADGE_CATEGORY_METADATA[category as BadgeCategory];
  return metadata?.name || category.replace(/_/g, ' ');
}

// Helper to get category description
export function getCategoryDescription(category: string): string {
  const metadata = BADGE_CATEGORY_METADATA[category as BadgeCategory];
  return metadata?.description || '';
}

// Get all badge categories
export function getAllBadgeCategories(): BadgeCategory[] {
  return Object.keys(BADGE_CATEGORY_METADATA) as BadgeCategory[];
}

// Get tier information for a category
export function getCategoryTiers(category: string) {
  const metadata = BADGE_CATEGORY_METADATA[category as BadgeCategory];
  return metadata?.tiers || [];
}

// Universal Tier System - Single Source of Truth
// Per gamification spec V2: Tier 1 (Bronze), Tier 2 (Silver), Tier 3 (Gold), Tier 4 (Purple)
// Tier 1: Bronze (Matte) - Entry level
// Tier 2: Silver (Satin) - Progress
// Tier 3: Gold (Gloss) - Skill
// Tier 4: Purple (Metallic w/ sparkle) - Mastery
export const UNIVERSAL_TIER_CONFIG = {
  tier1: {
    name: 'Tier 1',
    displayName: 'Bronze',
    finish: 'Matte',
    meaning: 'Entry level',
    textColor: 'text-amber-900 dark:text-amber-200',
    badgeGradient: 'bg-gradient-to-br from-amber-600 to-orange-700 dark:from-amber-800 dark:to-orange-900',
    barGradient: 'bg-gradient-to-r from-amber-600 to-orange-700',
    cardGradient: 'from-amber-600/20 to-amber-700/10 border-amber-600/30',
  },
  tier2: {
    name: 'Tier 2',
    displayName: 'Silver',
    finish: 'Satin',
    meaning: 'Progress',
    textColor: 'text-slate-700 dark:text-slate-200',
    badgeGradient: 'bg-gradient-to-br from-slate-300 to-gray-400 dark:from-slate-400 dark:to-gray-500',
    barGradient: 'bg-gradient-to-r from-slate-300 to-gray-400',
    cardGradient: 'from-slate-300/20 to-gray-400/10 border-slate-300/30',
  },
  tier3: {
    name: 'Tier 3',
    displayName: 'Gold',
    finish: 'Gloss',
    meaning: 'Skill',
    textColor: 'text-yellow-900 dark:text-yellow-100',
    badgeGradient: 'bg-gradient-to-br from-yellow-400 to-amber-500 dark:from-yellow-600 dark:to-amber-700',
    barGradient: 'bg-gradient-to-r from-yellow-400 to-amber-500',
    cardGradient: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30',
  },
  tier4: {
    name: 'Tier 4',
    displayName: 'Purple',
    finish: 'Metallic w/ sparkle',
    meaning: 'Mastery',
    textColor: 'text-purple-700 dark:text-purple-300',
    badgeGradient: 'bg-gradient-to-br from-purple-500 to-violet-600 dark:from-purple-700 dark:to-violet-800',
    barGradient: 'bg-gradient-to-r from-purple-500 to-violet-600',
    cardGradient: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
  },
} as const;

// Tier display colors (for badges/text in BadgeExplainerModal)
// Per gamification spec V2: Bronze → Silver → Gold → Purple
export const TIER_COLORS = {
  bronze: 'text-amber-600',
  silver: 'text-slate-400',
  gold: 'text-yellow-500',
  platinum: 'text-purple-500',
} as const;

// Badge tier background gradient colors (for cards in BadgeExplainerModal)
// Maps tier names to UNIVERSAL_TIER_CONFIG gradients
export function getBadgeTierColor(tier: string): string {
  switch (tier.toLowerCase()) {
    case 'bronze': return UNIVERSAL_TIER_CONFIG.tier1.cardGradient;
    case 'silver': return UNIVERSAL_TIER_CONFIG.tier2.cardGradient;
    case 'gold': return UNIVERSAL_TIER_CONFIG.tier3.cardGradient;
    case 'platinum': 
    case 'purple': return UNIVERSAL_TIER_CONFIG.tier4.cardGradient;
    default: return 'from-primary/20 to-primary/10 border-primary/30';
  }
}
